import { SecondaryCTAButton } from '@buoyhealth/common.buoy-button';
import { HelpfulQuestion } from '@buoy-components/user-feedback';
import Grid from '@mui/material/Grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackStructuredEvent } from 'src/analytics';
import useAppContext from 'src/hooks/useAppContext';
import { getCategory } from 'src/interviews/analytics';
import CollapsibleResults from 'src/interviews/components/CollapsibleResults';
import DifferentialCard from 'src/interviews/components/DifferentialCard';
import MedicationCard from 'src/interviews/components/MedicationCard';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import { IS_LOCAL } from 'src/interviews/requests/settings';

import useExpandedDifferential from 'src/interviews/hooks/useExpandedDifferential';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import useInterview from 'src/interviews/hooks/useInterview';
import useTransition from 'src/interviews/hooks/useTransition';

import { formatSentenceCase } from 'src/interviews/utils/formatTitleCasing';
import MessageBox from '../../MessageBox';
import RichEndings from '../components/RichEndings';
import Pill from '../DiagnosisOfConcern/components/Pill';
import { ALARMING_MESSAGE } from '../Emergency/getCompletionMessage';
import { buildDifferentialFromPotentialCondition } from '../Emergency/utils';
import { trackViewMoreDDxClicked } from '../analytics';
import type { IDiagnosis, IInterviewResults } from 'src/packages/polaris/types';

const EXPANDED_DDX_FLAG = 'growth-public-site-expanded-ddx';

const SymptomsCompletion = ({ result }: { result: IInterviewResults }) => {
  const transition = useTransition();
  const interview = useInterview();
  const expandedDifferential = useExpandedDifferential();
  const expandedDdxFlagEnabled = useFeatureFlag({
    flagName: EXPANDED_DDX_FLAG,
    defaultValue: IS_LOCAL,
  });
  const { interviewSettings } = useAppContext();
  const [expandedDifferentialCount, setExpandedDifferentialCount] = useState(0);
  const noResults = !result.differential?.length && !result.alarm;
  const canContinue = !!result._links?.continuation && !result.differential.length;
  const hasExpandedDifferential = !!expandedDifferential?.length;
  const potentialConditionAsDifferential: IDiagnosis | null = useMemo(
    () => buildDifferentialFromPotentialCondition(result),
    [result],
  );

  useEffect(() => {
    if (expandedDdxFlagEnabled) {
      transition({
        event: 'EXPAND',
        data: {
          ddxViewed: expandedDifferentialCount,
        },
      });
    }
  }, [expandedDifferentialCount]);

  const dismissAlarm = useCallback(() => {
    const symptomInterview = interviewSettings?.find(
      (interview) => interview.interview === 'symptoms',
    );

    transition({
      event: 'CONTINUE',
      data: {
        triage_rule_considered: result.triage.token,
        chief_complaint_id: symptomInterview?.configs?.[0]?.context?.complaints,
      },
    });
  }, [interviewSettings, result.triage.token, transition]);

  const incrementExpandedDifferentialCount = () => {
    trackViewMoreDDxClicked({
      interviewToken: interview.token || '',
      interviewType: 'symptoms',
    });
    setExpandedDifferentialCount((prevCount: number) => {
      // NOTE: default to increments of 2 due to performance
      const newCount = prevCount + 2;
      if (hasExpandedDifferential) {
        return Math.min(newCount, expandedDifferential.length);
      }
      return newCount;
    });
  };

  return (
    <Grid item className="!bp-max-w-full">
      <CollapsibleResults isShowingResults>
        <Grid container direction="column" spacing={2.5}>
          {noResults && (
            <Grid item>
              <Paragraph>
                Unfortunately I’m having trouble finding a match for what you’ve described. If your
                symptoms continue to bother you, consider contacting a medical professional.
              </Paragraph>
            </Grid>
          )}
          {result.alarm && (
            <>
              <MessageBox
                header="Alarming scenario warning"
                alarmMessage={ALARMING_MESSAGE}
                color="red"
              />
              {Boolean(result?.triage_explanation?.triage_factors?.length) && (
                <Grid item>
                  <Grid item className="bp-w-full">
                    <Paragraph className="!bp-mb-3 bp-w-full bp-text-base">
                      Alarming factors include:
                    </Paragraph>
                    <div className="bp-flex bp-w-full bp-flex-wrap bp-gap-2 bp-pb-2">
                      {result.triage_explanation.triage_factors?.map((label) => (
                        <Pill key={label} label={formatSentenceCase(label)} variant="check" />
                      ))}
                    </div>
                  </Grid>
                </Grid>
              )}
              {potentialConditionAsDifferential && (
                <Grid item>
                  <Paragraph className="!bp-mb-3 bp-w-full bp-text-base">
                    This could be an urgent situation like:
                  </Paragraph>
                  {potentialConditionAsDifferential && (
                    <DifferentialCard differential={potentialConditionAsDifferential} />
                  )}
                </Grid>
              )}
              {canContinue && (
                <>
                  <Grid item>
                    <Paragraph className="bp-font-primary-main">
                      If this doesn’t seem right, you may continue with your visit.
                    </Paragraph>
                  </Grid>
                  <Grid item>
                    <SecondaryCTAButton
                      fullWidth
                      className="!bp-rounded-[8px] !bp-px-[20px] !bp-py-[10px]"
                      onClick={dismissAlarm}
                    >
                      Dismiss and continue
                    </SecondaryCTAButton>
                    <Paragraph className="bp-font-primary-smaller bp-mt-3 bp-italic">
                      By clicking “Dismiss and continue,” you acknowledge that you have been alerted
                      to a potentially dangerous situation based on your visit answers and are
                      choosing to continue with your visit.
                    </Paragraph>
                  </Grid>
                </>
              )}
            </>
          )}
          {Boolean(result.differential.length) && (
            <>
              <Grid item>
                <Paragraph>
                  Based on what you&apos;ve told us, the most likely conditions are:
                </Paragraph>
              </Grid>
              <Grid item>
                <Grid container spacing={1}>
                  {result.differential?.map((d) => (
                    <DifferentialCard key={d.token} differential={d} showTreatmentPlan />
                  ))}
                  {result?.submitted_medications?.length > 0 && <MedicationCard />}
                </Grid>
              </Grid>
              {expandedDdxFlagEnabled && expandedDifferentialCount === 0 && (
                <Grid item>
                  <SecondaryCTAButton
                    fullWidth
                    className="!bp-rounded-[8px] !bp-px-[20px] !bp-py-[10px]"
                    onClick={incrementExpandedDifferentialCount}
                    disabled={expandedDifferentialCount === (expandedDifferential?.length || 0)}
                  >
                    See other possible conditions
                  </SecondaryCTAButton>
                </Grid>
              )}
            </>
          )}

          {expandedDdxFlagEnabled && hasExpandedDifferential && expandedDifferentialCount > 0 && (
            <>
              <Grid item>
                <Paragraph>Other conditions that were considered: </Paragraph>
              </Grid>
              <Grid item>
                <Grid container spacing={1}>
                  {/*TODO: Add support for treatment plans for likely matches*/}
                  {expandedDifferential
                    ?.slice(0, expandedDifferentialCount)
                    .map((d: any) => (
                      <DifferentialCard key={d.token} differential={d} showTreatmentPlan={false} />
                    ))}
                </Grid>
              </Grid>
              {
                <Grid item>
                  <SecondaryCTAButton
                    fullWidth
                    className="!bp-rounded-[8px] !bp-px-[20px] !bp-py-[10px]"
                    onClick={incrementExpandedDifferentialCount}
                    disabled={expandedDifferentialCount === (expandedDifferential?.length || 0)}
                  >
                    See more conditions
                  </SecondaryCTAButton>
                </Grid>
              }
            </>
          )}

          <Grid item>
            <HelpfulQuestion
              snowplowProps={{
                action: getCategory('symptoms'),
                label: interview.token || '',
              }}
              trackingCallback={trackStructuredEvent}
              withBuoyTheme
            />
          </Grid>
        </Grid>
        <RichEndings interviewType="symptoms" result={result} />
      </CollapsibleResults>
    </Grid>
  );
};

export default SymptomsCompletion;
